import { Fragment, useState } from 'react';
import SizeUnit from './SizeUnit';
import { UnitsProvider } from './Units';
import SizeUnitLabel from './SizeUnitLabel';
import SizeUnitSwitcher from './SizeUnitSwitcher';

import hoodieImage from './assets/pullover-size-chart.png';
import sweaterImage from './assets/sweater-size-chart.png';
import shirtImage from './assets/shirt_diagram.png';
import sizeChartData from './sizeChartData';
import { Tab } from '@headlessui/react';
import classNames from 'classnames';
import { Trans, useLingui } from '@lingui/react/macro';

export default function SizeChart() {
  const { t } = useLingui();
  const [activeTab, setActiveTab] = useState(0);
  const getInch = (cm) => Math.round(cm * 0.787402) / 2;
  const images = { 3: hoodieImage, 4: sweaterImage, 5: hoodieImage };

  return (
    <UnitsProvider>
      <p className="text-sm">
        <Trans>
          Getting the perfect fit is easy. Just grab your favourite tee shirt,
          lay it flat on a table, take a few measurements and pick the closest
          matching shirt. If you are in between 2 sizes always go on the larger,
          chances are your favourite tee has shrunk a little since you got it.
          Shirt sizes can vary by +/- 3cm, and will shrink by 1-3 cm over time.
          If in doubt, go on the bigger size!
        </Trans>
      </p>
      <p className="text-sm">
        <strong>
          <Trans>
            WOMEN (especially in the US and UK) Some customers say our shirts
            fit small compared to what they are used to (sometimes called Junior
            fit) so you may need to order up 1-2 sizes from usual. If in doubt
            compare to a tee you own that fit you. Thanks!
          </Trans>
        </strong>
      </p>

      <div className="w-full">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="flex-[3] p-4">
            <img src={images[activeTab] || shirtImage} alt="" />
          </div>
          <div className="flex-[4] w-full">
            {[3, 4, 5].includes(activeTab) ? (
              <div id="hoodie-size-blurb">
                <h2 className="text-lg font-bold">
                  <Trans>Hoodie/sweater Sizing:</Trans>
                </h2>
                <p className="text-sm">
                  <Trans>
                    Our hoodie/sweater sizing broadly matches our Unisex tees so
                    if you wear a Medium in them, choose a Medium Hoodie/Sweater
                    also. To pick your closest size measure your chest size
                    (that's all the way around your chest at it's widest part
                    under your arms and compare it to the chart below picking
                    the closest size. As with our tees, our Hoodies/Sweater tend
                    to follow a slimmer fit, so if in doubt or between two sizes
                    we'd always recommend ordering the larger one.
                  </Trans>
                </p>
              </div>
            ) : (
              <div id="tee-size-blurb">
                <h2 className="text-lg font-bold">
                  <Trans>Shirt Width:</Trans>
                </h2>
                <p className="text-sm">
                  <Trans>
                    Lay your shirt flat on a table and measure the width under
                    the arms (Measurement A on diagram)
                  </Trans>
                </p>
                <h2 className="text-lg font-bold">
                  <Trans>Shirt Length:</Trans>
                </h2>
                <p className="text-sm">
                  <Trans>
                    Measure the length of your t-shirt from the seam at the neck
                    to the bottom of the garment. (Measurement B on diagram)
                  </Trans>
                </p>
              </div>
            )}
            <div className="pt-4">
              <Tab.Group onChange={(index) => setActiveTab(index)}>
                <Tab.List className="flex">
                  {sizeChartData.map(({ title, eventKey }) => (
                    <Tab as={Fragment} key={eventKey}>
                      {({ selected }) => (
                        <div
                          className={classNames(
                            'flex-1 border-b flex justify-center items-center p-2 text-xs font-bold border-qw-border cursor-pointer',
                            {
                              'border border-b-0': selected,
                            }
                          )}
                        >
                          {title}
                        </div>
                      )}
                    </Tab>
                  ))}
                </Tab.List>
                <Tab.Panels>
                  {sizeChartData.map(({ title, eventKey, sizes }) => (
                    <Tab.Panel key={eventKey} title={title}>
                      <table
                        className="text-xs"
                        id="sizing-tabs-mens"
                        size="sm"
                      >
                        <tbody>
                          <Fragment>
                            <tr>
                              <th key="size" className="py-1 px-2">
                                <Trans>Sizes</Trans>
                              </th>
                              {sizes.map(({ size }) => (
                                <th key={size} className="py-1 px-2">
                                  {size}
                                </th>
                              ))}
                            </tr>
                            <tr>
                              <td className="py-1 px-2">
                                {t`Width`} (
                                <SizeUnitLabel className="length-unit" />)
                              </td>
                              {sizes.map(({ size, w }) => (
                                <td key={`${size}`} className="py-1 px-2">
                                  <SizeUnit cm={w} inches={getInch(w)} />
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="py-1 px-2">
                                {t`Length`} (
                                <SizeUnitLabel className="length-unit" />)
                              </td>
                              {sizes.map(({ size, l }) => (
                                <td key={`${size}`} className="py-1 px-2">
                                  <SizeUnit cm={l} inches={getInch(l)} />
                                </td>
                              ))}
                            </tr>
                          </Fragment>
                        </tbody>
                      </table>
                    </Tab.Panel>
                  ))}
                </Tab.Panels>
              </Tab.Group>
              <SizeUnitSwitcher />
            </div>
          </div>
        </div>
      </div>
    </UnitsProvider>
  );
}
